<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { VDrawerRoot, VDrawerRootNested } from '~kui/vendor/vaul/drawer'
import { DialogRoot } from 'reka-ui'
import { computed, onMounted, onUnmounted, ref, toRef, watch } from 'vue'

import { provideCredenzaContext, useCredenzaContext } from './credenzaContext'

const props = withDefaults(defineProps<Props>(), {
  open: undefined,
  dismissable: true,
  dismissOnEscape: undefined,
  fullScreen: false,
  nested: false,
  showDragHandle: false,
  showCloseButton: false,
  modal: true,
  animated: true,
})
const emit = defineEmits<{
  'update:open': [value: boolean];
}>()
const VAUL_DURATION_MS = 500
const VAUL_DURATION = `${VAUL_DURATION_MS / 1000}s`
const VAUL_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)'
const VAUL_BORDER_RADIUS = 8
const VAUL_TRANSLATE_Y = 14
const VAUL_INSET = 26

interface Props {
  open?: boolean;
  dismissable?: boolean;
  /**
   * Whether Escape closes it, which follows `dismissable` unless set. A credenza that must not be
   * dragged or clicked away can still owe a keyboard student the one key every dialog answers to.
   * Desktop only: the drawer ties Escape to its own drag dismissal.
   */
  dismissOnEscape?: boolean;
  fullScreen?: boolean;
  nested?: boolean;
  showDragHandle?: boolean;
  showCloseButton?: boolean;
  modal?: boolean;
  /**
   * Whether the open and close are animated. A consumer that mounts this already open — because
   * the URL it loaded says so — passes `false` for that first render, so the student arrives at a
   * credenza that is simply open rather than watching it slide up at a page they linked to.
   */
  animated?: boolean;
}

const breakpoints = useBreakpoints(breakpointsTailwind)
const liveMobile = breakpoints.smaller('md')
const lockedMobile = ref(liveMobile.value)
const closing = ref(false)
// Always read from lockedMobile so the dynamic Root (DialogRoot vs VDrawerRoot) never
// flips while the credenza is open or in its close animation. Unlocking on close
// (when liveMobile differs — e.g. viewport resize, scrollbar appearance) swaps the Reka
// UI Root mid-unmount and crashes the portal tree with null-vnode errors. Only safe to
// pick up viewport changes while fully idle.
const isMobile = computed(() => lockedMobile.value)
const dragProgress = ref(0)

provideCredenzaContext({
  isMobile,
  dismissable: toRef(props, 'dismissable'),
  dismissOnEscape: computed(() => props.dismissOnEscape ?? props.dismissable),
  fullScreen: toRef(props, 'fullScreen'),
  showDragHandle: toRef(props, 'showDragHandle'),
  showCloseButton: toRef(props, 'showCloseButton'),
  dragProgress,
  animated: toRef(props, 'animated'),
})

// A nested drawer root stacks on the drawer of the credenza it sits inside, so it only applies
// while that credenza is rendering as a drawer. Its breakpoint is locked independently of ours:
// after a desktop-to-phone resize an open parent stays a dialog while an idle child re-roots,
// and a nested root under a dialog has no drawer context to inject and throws.
const parentCredenza = useCredenzaContext(null)
const MobileRoot = computed(() =>
  props.nested && parentCredenza?.isMobile.value ? VDrawerRootNested : VDrawerRoot,
)
const Root = computed(() =>
  isMobile.value ? MobileRoot.value : DialogRoot,
)

let settleTimer: ReturnType<typeof setTimeout> | undefined

// `open` goes false the moment the close starts, but Presence keeps the content mounted for
// the whole slide/fade out, so the lock has to be held for the animation's length past that
// before liveMobile is allowed back in. A reopen inside that window cancels the pending settle
// and keeps the lock rather than taking the live breakpoint: the outgoing tree is still
// unmounting, and swapping the Root under it is the very crash the lock exists to prevent. The
// trade-off is that such a reopen renders in the outgoing breakpoint's chrome until the
// credenza is next fully idle. The lock only ever takes a new breakpoint from an idle state —
// the settle below, the closed-and-settled watcher, or the ref's initial value.
watch(() => props.open, (open) => {
  clearTimeout(settleTimer)
  if (open) {
    closing.value = false
    return
  }
  closing.value = true
  settleTimer = setTimeout(() => {
    closing.value = false
    lockedMobile.value = liveMobile.value
  }, VAUL_DURATION_MS)
})

watch(liveMobile, (mobile) => {
  if (!props.open && !closing.value) {
    lockedMobile.value = mobile
  }
})

const onUpdateOpen = (v: boolean) => {
  if (v) {
    dragProgress.value = 0
  }
  emit('update:open', v)
}

const onDrag = (percentage: number) => {
  dragProgress.value = Math.min(1, Math.max(0, percentage))
}

const onRelease = () => {
  dragProgress.value = 0
}

let cleanupTimer: ReturnType<typeof setTimeout> | undefined

const getWrapper = () =>
  document.querySelector('[data-vaul-drawer-wrapper]') as HTMLElement | null

const applyDesktopScale = (wrapper: HTMLElement) => {
  const scale = (window.innerWidth - VAUL_INSET) / window.innerWidth
  Object.assign(wrapper.style, {
    transform: `scale(${scale}) translate3d(0, ${VAUL_TRANSLATE_Y}px, 0)`,
    transformOrigin: 'top',
    borderRadius: `${VAUL_BORDER_RADIUS}px`,
    overflow: 'hidden',
    transitionProperty: 'transform, border-radius',
    transitionDuration: VAUL_DURATION,
    transitionTimingFunction: VAUL_EASING,
  })
}

const removeDesktopScale = (wrapper: HTMLElement) => {
  Object.assign(wrapper.style, {
    transform: '',
    borderRadius: '',
  })
  clearTimeout(cleanupTimer)
  cleanupTimer = setTimeout(() => {
    Object.assign(wrapper.style, {
      transitionProperty: '',
      transitionDuration: '',
      transitionTimingFunction: '',
      transformOrigin: '',
      overflow: '',
    })
  }, VAUL_DURATION_MS)
}

watch(() => props.open, (open) => {
  if (isMobile.value || !props.fullScreen) {
    return
  }
  const wrapper = getWrapper()
  if (!wrapper) {
    return
  }

  if (open) {
    applyDesktopScale(wrapper)
  }
  else {
    removeDesktopScale(wrapper)
  }
})

onMounted(() => {
  const wrapper = document.querySelector('#__nuxt')
    || document.querySelector('#app')
    || document.querySelector('#storybook-root')
    || document.body.firstElementChild
  if (wrapper && !wrapper.hasAttribute('data-vaul-drawer-wrapper')) {
    wrapper.setAttribute('data-vaul-drawer-wrapper', '')
  }
})

onUnmounted(() => {
  clearTimeout(cleanupTimer)
  clearTimeout(settleTimer)
  if (!isMobile.value && props.fullScreen) {
    const wrapper = getWrapper()
    if (wrapper) {
      Object.assign(wrapper.style, {
        transform: '',
        borderRadius: '',
        overflow: '',
        transitionProperty: '',
        transitionDuration: '',
        transitionTimingFunction: '',
        transformOrigin: '',
      })
    }
  }
})

const propsToBind = computed(() =>
  isMobile.value
    ? {
        shouldScaleBackground: true,
        setBackgroundColorOnScale: false,
        closeThreshold: 0.35,
        dismissible: props.dismissable,
      }
    : { modal: props.modal },
)
</script>

<template>
  <ClientOnly>
    <component
      :is="Root"
      :open="open"
      v-bind="propsToBind"
      @update:open="onUpdateOpen"
      @drag="onDrag"
      @release="onRelease"
    >
      <slot />
    </component>
  </ClientOnly>
</template>
